import Swal from 'sweetalert2';
import { ref } from 'vue';
import { useModal, useModalSlot, VueFinalModal } from 'vue-final-modal';
// Interfaces
import type { DocumentRevision } from '@/entities/Document';
import { documentApi } from '@/entities/Document';
import { DocumentRevisionStatus, useCustomToast } from '@/shared/lib';
import { buildDocumentShareUrl } from './buildDocumentShareUrl';
import { EditHomologationModal, useDocuments } from '@/features/Document';
import DocumentControlModal from '../ui/DocumentControlModal.vue';
import {
  DocumentHistoryModal,
  ExternalDocumentEditModal,
  InternalDocumentEditModal,
  DocumentTrainingModal,
} from '..';
import { mapEditDocumentModalAttrs } from './edit-document-modal-attrs.mapper';

const MODAL_CENTERED_CLASS = 'flex justify-center items-center';

export function useDocumentActions(onDocumentSaved?: () => void) {
  const toast = useCustomToast();
  const { getRevisionFileInfo } = useDocuments();
  const isLoadingEditInternal = ref(false);
  const isLoadingEditExternal = ref(false);
  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      await Swal.fire({
        title: 'URL copiada para a área de transferência',
        icon: 'success',
        timer: 1500,
      });
    } catch {
      await Swal.fire({
        title: 'Erro ao copiar URL para a área de transferência',
        icon: 'error',
        timer: 1500,
      });
    }
  };

  const getCurrentRevisionStatus = (revision: DocumentRevision) => {
    return revision.document.revisions && revision.document.revisions.length > 0
      ? revision.document.revisions.reduce(
          (latest, current) => (current.revision > latest.revision ? current : latest),
          revision.document.revisions[0]
        ).status
      : null;
  };

  const setUrl = async (currentStatus: DocumentRevisionStatus | null, revisionId: number) => {
    const baseUrl = globalThis.location.origin;

    if (currentStatus === DocumentRevisionStatus.PENDING_APPROVAL) {
      return `${baseUrl}/aprovacao/${revisionId}`;
    } else if (currentStatus === DocumentRevisionStatus.PENDING_VERIFICATION) {
      return `${baseUrl}/verificacao/${revisionId}`;
    }

    await Swal.fire({
      title: 'Erro ao copiar URL para a área de transferência',
      icon: 'error',
      timer: 1500,
    });

    return '';
  };

  const copyDocumentDetails = async (revision: DocumentRevision) => {
    const currentStatus = getCurrentRevisionStatus(revision);
    const url = await setUrl(currentStatus, revision.id); // TODO(HTMDOC-1493): Precisamos normalizar a URL para o ambiente de produção (Adicionar variável de ambiente)

    await copyToClipboard(url);
  };

  const copyDocumentLink = async (revision: Pick<DocumentRevision, 'id' | 'documentId'>) => {
    try {
      const fileInfo = await getRevisionFileInfo(revision.documentId.toString(), revision.id);
      if (!fileInfo) {
        throw new Error('Revisão não encontrada');
      }

      const url = buildDocumentShareUrl({
        documentId: revision.documentId,
        docUrl: fileInfo.docUrl,
        minioObjectName: fileInfo.minioObjectName,
      });

      await navigator.clipboard.writeText(url);
      toast.showSuccessToast({ title: 'Link copiado', type: 'success' });
    } catch {
      toast.showErrorToast({ title: 'Não foi possível copiar o link', type: 'error' });
    }
  };

  const openInfo = async (title: string, type: string) => {
    await Swal.fire({
      title: title,
      icon: 'info',
      html: `<span>${type}</span>`,
    });
  };

  const openDocumentPdf = (revision: DocumentRevision) => {
    if (revision.docUrl) {
      window.open(revision.docUrl, '_blank');
    }
  };

  const historyModal = useModal({
    component: VueFinalModal,
    attrs: {
      class: MODAL_CENTERED_CLASS,
      overlayTransition: 'vfm-fade',
      contentTransition: 'vfm-fade',
    },
    slots: {
      default: useModalSlot({
        component: DocumentHistoryModal,
        attrs: {
          title: 'Histórico de documentos',
          revision: null,
          onClose: () => {
            historyModal.close();
          },
        },
      }),
    },
  });

  const controlModal = useModal({
    component: VueFinalModal,
    attrs: {
      class: MODAL_CENTERED_CLASS,
      overlayTransition: 'vfm-fade',
      contentTransition: 'vfm-fade',
    },
    slots: {
      default: useModalSlot({
        component: DocumentControlModal,
        attrs: {
          title: 'Controle de documentos',
          revision: null,
          onClose: () => {
            controlModal.close();
          },
        },
      }),
    },
  });

  const editExternalModal = useModal({
    component: VueFinalModal,
    attrs: {
      class: MODAL_CENTERED_CLASS,
      overlayTransition: 'vfm-fade',
      contentTransition: 'vfm-fade',
    },
    slots: {
      default: useModalSlot({
        component: ExternalDocumentEditModal,
        attrs: {
          title: 'Editar/Configurar Documento',
          revision: null,
          onClose: () => {
            editExternalModal.close();
          },
        },
      }),
    },
  });

  const editInternalModal = useModal({
    component: VueFinalModal,
    attrs: {
      class: MODAL_CENTERED_CLASS,
      overlayTransition: 'vfm-fade',
      contentTransition: 'vfm-fade',
    },
    slots: {
      default: useModalSlot({
        component: InternalDocumentEditModal,
        attrs: {
          title: 'Editar/Configurar Documento',
          revision: null,
          onClose: () => {
            editInternalModal.close();
          },
          onSaved: () => {
            editInternalModal.close();
            onDocumentSaved?.();
          },
        },
      }),
    },
  });

  const trainingModal = useModal({
    component: VueFinalModal,
    attrs: {
      class: MODAL_CENTERED_CLASS,
      overlayTransition: 'vfm-fade',
      contentTransition: 'vfm-fade',
    },
    slots: {
      default: useModalSlot({
        component: DocumentTrainingModal,
        attrs: {
          title: 'Controle de treinamento',
          revision: null,
          onClose: () => {
            trainingModal.close();
          },
        },
      }),
    },
  });

  const editHomologationModal = useModal({
    component: VueFinalModal,
    attrs: {
      class: MODAL_CENTERED_CLASS,
      overlayTransition: 'vfm-fade',
      contentTransition: 'vfm-fade',
    },
    slots: {
      default: useModalSlot({
        component: EditHomologationModal,
        attrs: {
          title: 'Editar Homologação',
          revision: null,
          onClose: () => {
            editHomologationModal.close();
          },
          onSaved: () => {
            editHomologationModal.close();
          },
        },
      }),
    },
  });

  const fetchFullRevision = async (revisionId: number) => {
    return await documentApi.getRevisionById(revisionId);
  };

  const buildEditInternalModalAttrs = async (
    revision: DocumentRevision,
    attrs: Record<string, unknown>
  ) => {
    isLoadingEditInternal.value = true;
    try {
      const fetched = await fetchFullRevision(revision.id);
      return mapEditDocumentModalAttrs(revision, attrs, fetched, 'internal');
    } finally {
      isLoadingEditInternal.value = false;
    }
  };

  const buildEditExternalModalAttrs = async (
    revision: DocumentRevision,
    attrs: Record<string, unknown>
  ) => {
    isLoadingEditExternal.value = true;
    try {
      const fetched = await fetchFullRevision(revision.id);
      return mapEditDocumentModalAttrs(revision, attrs, fetched, 'external');
    } finally {
      isLoadingEditExternal.value = false;
    }
  };

  const getModalConfig = (
    modal: 'history' | 'control' | 'editExternal' | 'editInternal' | 'training' | 'editHomologation'
  ): {
    modalInstance: ReturnType<typeof useModal>;
    component: any;
    defaultTitle: string;
  } => {
    const modals: Record<string, any> = {
      history: { modal: historyModal, component: DocumentHistoryModal, defaultTitle: '' },
      control: { modal: controlModal, component: DocumentControlModal, defaultTitle: '' },
      editExternal: {
        modal: editExternalModal,
        component: ExternalDocumentEditModal,
        defaultTitle: 'Editar/Configurar Documento',
      },
      editInternal: {
        modal: editInternalModal,
        component: InternalDocumentEditModal,
        defaultTitle: 'Editar/Configurar Documento',
      },
      training: {
        modal: trainingModal,
        component: DocumentTrainingModal,
        defaultTitle: 'Controle de treinamento',
      },
      editHomologation: {
        modal: editHomologationModal,
        component: EditHomologationModal,
        defaultTitle: 'Editar Homologação',
      },
    };
    const config = modals[modal];
    return {
      modalInstance: config.modal as ReturnType<typeof useModal>,
      component: config.component,
      defaultTitle: config.defaultTitle,
    };
  };

  const createModalAttrs = (
    modalType: string,
    modalInstance: ReturnType<typeof useModal>,
    revision: DocumentRevision,
    title: string
  ) => {
    const baseAttrs = {
      title,
      revision,
      onClose: () => {
        modalInstance.close();
      },
    };

    if (modalType === 'editInternal') {
      return {
        ...baseAttrs,
        onSaved: () => {
          editInternalModal.close();
          onDocumentSaved?.();
        },
      };
    }
    if (modalType === 'editExternal') {
      return {
        ...baseAttrs,
        onSaved: () => {
          editExternalModal.close();
          onDocumentSaved?.();
        },
      };
    }
    if (modalType === 'editHomologation') {
      return {
        ...baseAttrs,
        onSaved: () => {
          editHomologationModal.close();
          onDocumentSaved?.();
        },
      };
    }
    return baseAttrs;
  };

  const openModal = async (
    revision: DocumentRevision,
    modal:
      | 'history'
      | 'control'
      | 'editExternal'
      | 'editInternal'
      | 'training'
      | 'editHomologation',
    attrs: Record<string, unknown>
  ) => {
    const { modalInstance, component, defaultTitle } = getModalConfig(modal);

    let finalAttrs: { title: string; revision: DocumentRevision };
    if (modal === 'editInternal') {
      finalAttrs = await buildEditInternalModalAttrs(revision, attrs);
    } else if (modal === 'editExternal') {
      finalAttrs = await buildEditExternalModalAttrs(revision, attrs);
    } else {
      const title = typeof attrs.title === 'string' ? attrs.title : defaultTitle;
      finalAttrs = { title, revision };
    }

    const title = typeof finalAttrs.title === 'string' ? finalAttrs.title : defaultTitle;
    const modalAttrs = createModalAttrs(modal, modalInstance, finalAttrs.revision, title);

    modalInstance.patchOptions({
      slots: {
        default: useModalSlot({
          component,
          attrs: modalAttrs,
        }),
      },
    });

    return modalInstance.open();
  };

  const closeModal = (modal: 'history' | 'control' | 'training') => {
    const modals = {
      history: historyModal,
      control: controlModal,
      training: trainingModal,
    };
    modals[modal].close();
  };

  return {
    copyToClipboard,
    openInfo,
    openDocumentPdf,
    openModal,
    closeModal,
    copyDocumentDetails,
    copyDocumentLink,
    isLoadingEditInternal,
    isLoadingEditExternal,
  };
}
